import type { Pt } from "../cable-routing";
import { obstaclesForCable, tidyRoute, tidySystemCables, type RoutePlanner, type EscapePlanner } from "../cable-tidy";
import { endpointPos } from "../store-helpers";
import { planEscapeRoute, type Rect } from "../cable-escape";
import type { Cable, SystemNode } from "../types";
import { LegacyCableRouter } from "./legacy-router";
import { searchOrthogonalRoute } from "./orthogonal-search";
import type { CableRouteRequest, ConnectionRouteRequest, RoutingScene } from "./types";

/** Keep candidate/corridor scoring and lane separation together. Only a route
 * that needs a multi-bend obstacle escape pays for visibility-grid A*. */
const graphEscape: EscapePlanner = (source, sourceSide, target, targetSide, hard, self, soft, sourceStub, targetStub) =>
  searchOrthogonalRoute({ source, sourceSide, target, targetSide, hard, self, soft, sourceStub, targetStub }) ??
    planEscapeRoute(source, sourceSide, target, targetSide, hard, self, soft, sourceStub, targetStub);

const graphPlanner: RoutePlanner = (
  source, sourceSide, target, targetSide, hard, self = [], others = [],
  sourceSplay = 0, targetSplay = 0, soft = [], corridors = [], shared,
) => tidyRoute(source, sourceSide, target, targetSide, hard, self, others,
  sourceSplay, targetSplay, soft, corridors, shared, graphEscape);

export class OrthogonalCableRouter extends LegacyCableRouter {
  readonly id = "orthogonal" as const;
  private readonly routes = new WeakMap<Cable, { request: CableRouteRequest; points: Pt[] }>();

  route(request: CableRouteRequest): Pt[] {
    const previous = request.cable ? this.routes.get(request.cable) : undefined;
    const old = previous?.request;
    if (old && old.scene === request.scene && old.phase === request.phase &&
        old.movingEndpoint === request.movingEndpoint &&
        old.sourceIsJunction === request.sourceIsJunction && old.targetIsJunction === request.targetIsJunction &&
        old.sourceSide === request.sourceSide && old.targetSide === request.targetSide &&
        old.source.x === request.source.x && old.source.y === request.source.y &&
        old.target.x === request.target.x && old.target.y === request.target.y) return previous.points;
    const points = super.route(request);
    if (request.cable) this.routes.set(request.cable, { request, points });
    return points;
  }


  private readonly previews = new WeakMap<RoutingScene, WeakMap<Cable, {
    obstacles: ReturnType<typeof obstaclesForCable>;
    source: Pt | null;
    target: Pt | null;
  }>>();

  protected preview(request: CableRouteRequest): Pt[] {
    const { scene, cable, source, sourceSide, target, targetSide } = request;
    if (!scene || !cable) return this.connection(request);
    let byCable = this.previews.get(scene);
    if (!byCable) {
      byCable = new WeakMap();
      this.previews.set(scene, byCable);
    }
    let prepared = byCable.get(cable);
    if (!prepared) {
      prepared = {
        obstacles: obstaclesForCable(cable, scene),
        source: scene.nodes ? endpointPos(cable.fromNodeId, cable.fromTerminalId, scene.nodes) : null,
        target: scene.nodes ? endpointPos(cable.toNodeId, cable.toTerminalId, scene.nodes) : null,
      };
      byCable.set(cable, prepared);
    }
    const { hard, soft } = prepared.obstacles;
    const self: Rect[] = [];
    const move = (id: string, from: Pt | null, to: Pt) => {
      const rect = scene.self.get(id);
      if (!rect) return;
      const dx = request.movingEndpoint && from ? to.x - from.x : 0;
      const dy = request.movingEndpoint && from ? to.y - from.y : 0;
      self.push({ ...rect, x: rect.x + dx, y: rect.y + dy,
        core: rect.core ? { ...rect.core, x: rect.core.x + dx, y: rect.core.y + dy } : undefined });
    };
    // Keep stationary obstacles during drag, but move each endpoint's body
    // with its live terminal instead of routing around its old rectangle. A
    // stud dragged along a stationary body must not translate that body.
    move(cable.fromNodeId, prepared.source, source);
    if (cable.toNodeId !== cable.fromNodeId) move(cable.toNodeId, prepared.target, target);
    return graphPlanner(source, sourceSide, target, targetSide, hard, self, [], 0, 0, soft);
  }

  private quickConnection(request: ConnectionRouteRequest): Pt[] {
    return searchOrthogonalRoute({ ...request, hard: [] }) ?? super.connection(request);
  }

  connection(request: ConnectionRouteRequest): Pt[] {
    const { source, sourceSide, target, targetSide, scene, endpoints } = request;
    if (!scene || !endpoints) return this.quickConnection(request);
    const { hard, self, soft } = obstaclesForCable(endpoints, scene);
    return graphPlanner(source, sourceSide, target, targetSide, hard, self, [], 0, 0, soft);
  }

  settle(nodes: SystemNode[], cables: Cable[], ids: string[]): Map<string, Pt[]> {
    return tidySystemCables(nodes, cables, ids, graphPlanner);
  }
}
